import clsx from 'clsx';
import { isNil, uniqueId } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu, type MenuProps, styled } from 'netline-ui';
import { ProductsMenuItem } from 'types';

import styles, { productMenuClasses } from './ProductsMenu.styles';
import ProductsMenuItemType from './ProductsMenuItem';

const ProductMenuRoot = styled(Menu, { name: productMenuClasses.$name })(styles);

type ProductMenuProps = {
  products: Map<string, ProductsMenuItem[]>;
  onClose: () => void;
} & MenuProps;

const ProductsMenu = forwardRef<HTMLDivElement, ProductMenuProps>(({ className, products, onClose, ...props }, ref) => {
  const {
    t,
    i18n: { exists },
  } = useTranslation();

  const handleMenuItemClick = useCallback(
    (product /* , event */) => {
      window.postMessage({ source: 'productsMenu', type: 'menuitemclick', payload: product }, window.location.origin);
      onClose();
    },
    [onClose],
  );

  return (
    <ProductMenuRoot
      ref={ref}
      className={clsx(className, productMenuClasses.root)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      marginThreshold={0}
      {...props}
    >
      {Array.from(products).map(([category, productsInCategory]) => {
        const isCategoryVisible = !isNil(category) && productsInCategory.length > 0;

        return (
          <div key={category ?? uniqueId('category_')}>
            {isCategoryVisible && (
              <div className={productMenuClasses.categoryTitle}>
                {exists(`products_menu.categories.${category}`) ? t(`products_menu.categories.${category}`) : category}
              </div>
            )}
            {productsInCategory.map((product) => {
              return (
                <ProductsMenuItemType
                  key={product.id}
                  className={productMenuClasses.menuItem}
                  product={product}
                  onMenuItemClick={handleMenuItemClick}
                />
              );
            })}
          </div>
        );
      })}
    </ProductMenuRoot>
  );
});

ProductsMenu.propTypes = {
  className: PropTypes.string,
  products: PropTypes.instanceOf(Map<string, ProductsMenuItem[]>).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProductsMenu;
